import { BUILT_IN_PRESETS, generateCustomPreset, validatePresetKey } from "@/design-system/presets";
import { themeableTokens, type AdaptiveColor, type ThemeableTokenKey } from "@/design-system/tokens";

export const APPEARANCE_THEME_STORAGE_KEY = "sablcrm-theme";
export const COLOR_THEME_STORAGE_KEY = "sablcrm-color-theme";
export const COLOR_THEME_CSS_STORAGE_KEY = "sablcrm-color-theme-css";
export const COLOR_THEME_STYLE_ID = "design-system-overrides";

export interface CustomThemeColors {
    primary: string;
    accent: string;
    primaryDark?: string;
    accentDark?: string;
    background?: string;
    muted?: string;
    card?: string;
    /** Explicit dark-mode overrides (2026-07-31 fidelity engine); absent
     *  slots derive from the light pick. Supersedes the flat primaryDark/
     *  accentDark pair, which is folded in below for compatibility. */
    dark?: { primary?: string; accent?: string; background?: string; muted?: string; card?: string };
}

function buildStyleSheet(colors: Record<ThemeableTokenKey, AdaptiveColor>): string {
    const lightVars = Object.entries(colors).map(([k, v]) => `  --${k}: ${(v as AdaptiveColor).light};`).join("\n");
    const darkVars = Object.entries(colors).map(([k, v]) => `  --${k}: ${(v as AdaptiveColor).dark};`).join("\n");

    // GOTCHA: the persisted dark-mode class is `dw-dark` (next-themes value map
    // in theme-provider.tsx), so it MUST be in the dark selector — a `.dark`-only
    // block never matches after a reload, and the `:root` light block then beats
    // tokens.css's `.dw-dark` by document order (equal specificity, injected
    // <style> comes later), rendering every injected preset light in dark mode.
    // `.dark` stays for the view-transition frame (ThemeTogglerPrimitive) and
    // element-scoped dark previews (workbench panes toggle `dark` on a div).
    return `:root {\n${lightVars}\n}\n\n.dark,\n.dw-dark {\n${darkVars}\n}`;
}

/** The colors a theme's sheet carries — null when the theme emits no sheet and tokens.css's own
 *  colors stand: the default theme, or a custom theme with nothing chosen yet. */
function themeSheetColors(key: string | undefined | null, customThemeColors: CustomThemeColors | null | undefined): Record<ThemeableTokenKey, AdaptiveColor> | null {
    const presetKey = validatePresetKey(key);

    if (presetKey === "default") {
        return null;
    }

    if (presetKey === "custom") {
        if (!customThemeColors) {
            return null;
        }
        const dark = customThemeColors.dark || customThemeColors.primaryDark || customThemeColors.accentDark
            ? {
                primaryHex: customThemeColors.dark?.primary ?? customThemeColors.primaryDark,
                accentHex: customThemeColors.dark?.accent ?? customThemeColors.accentDark,
                backgroundHex: customThemeColors.dark?.background,
                mutedHex: customThemeColors.dark?.muted,
                cardHex: customThemeColors.dark?.card,
            }
            : undefined;
        return generateCustomPreset({ primaryHex: customThemeColors.primary, accentHex: customThemeColors.accent, backgroundHex: customThemeColors.background, mutedHex: customThemeColors.muted, cardHex: customThemeColors.card, dark }).colors;
    }

    const preset = BUILT_IN_PRESETS[presetKey];
    return preset ? preset.colors : null;
}

/** A theme's stylesheet: its tokens over tokens.css's default ones, the same sheet on every surface —
 *  nothing overrides a theme for one surface. The default theme emits none (tokens.css is it). */
export function resolvePresetStyleSheet(key: string | undefined | null, customThemeColors?: CustomThemeColors | null): string | null {
    const colors = themeSheetColors(key, customThemeColors);
    return colors ? buildStyleSheet(colors) : null;
}

/** The colors a theme paints, wherever it is worn: its sheet's, else tokens.css's own — the default
 *  theme's tokens (tokens.ts), which every surface wears. `BUILT_IN_PRESETS.default` is the legacy
 *  near-black picker theme, not what the default theme paints. */
export function resolvePresetColors(key: string | undefined | null, customThemeColors?: CustomThemeColors | null): Record<ThemeableTokenKey, AdaptiveColor> {
    return themeSheetColors(key, customThemeColors) ?? themeableTokens;
}
